import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Constants } from '@app/constants';
import { map } from 'rxjs';
import { momentCustomInstance as moment } from '../../utils/helper';
import { getContext } from '@app/core';

export enum TokenType {
  Voice,
  Chat,
  Worker,
}

@Injectable({ providedIn: 'root' })
export class TelephonyService {
  constructor(public http: HttpClient) {
  }

  getToken(identity: string, token: TokenType, accountSid: string, workerSid: string = '') {
    return this.http.disableLoader().post(
      Constants.APIS.TELEPHONY.GET_TOKEN,
      {
        Identity: identity,
        Type: token,
        AccountSid: accountSid,
        WorkerSid: workerSid,
      },
      { context: getContext('telephony') }
    );
  }
  getTwilioSettings() {
    return this.http
      .disableLoader()
      .get(Constants.APIS.TELEPHONY.TWILIO_SETTINGS, { context: getContext('telephony') });
  }
  getCallConference(accountSid: string, conferenceName: string = '') {
    return this.http.disableLoader().post(
      Constants.APIS.TELEPHONY.GET_CONFERENCE,
      {
        AccountSid: accountSid,
        FriendlyName: conferenceName,
      },
      { context: getContext('telephony') }
    );
  }
  consult(
    to: string,
    callerId: string,
    conferenceSid: string,
    callSid: string,
    accountSid: string,
    onConference: boolean
  ) {
    return this.http.post(
      Constants.APIS.TELEPHONY.CONSULT,
      {
        To: to,
        CallerId: callerId,
        ConferenceSid: conferenceSid,
        CustomerCallSid: callSid,
        OnConference: onConference,
        AccountSid: accountSid,
      },
      { context: getContext('telephony') }
    );
  }
  conference(
    to: string,
    callerId: string,
    conferenceSid: string,
    callSid: string,
    accountSid: string,
    OnConsult: boolean
  ) {
    return this.http.post(
      Constants.APIS.TELEPHONY.CONFERENCE,
      {
        To: to,
        CallerId: callerId,
        ConferenceSid: conferenceSid,
        CustomerCallSid: callSid,
        OnConsult: OnConsult,
        AccountSid: accountSid,
      },
      { context: getContext('telephony') }
    );
  }
  toggle(
    conferenceSid: string,
    callSid: string,
    accountSid: string,
    isConnectToCustomer: boolean,
    isAgentOnConsult: boolean
  ) {
    return this.http.post(
      Constants.APIS.TELEPHONY.TOGGLE,
      {
        IsConnectToCustomer: isConnectToCustomer,
        IsAgentOnConsult: isAgentOnConsult,
        ConferenceSid: conferenceSid,
        CustomerCallSid: callSid,
        AccountSid: accountSid,
      },
      { context: getContext('telephony') }
    );
  }
  endThirdParty(conferenceSid: string, callSid: string, accountSid: string, isAgentOnConsult: boolean) {
    return this.http.post(
      Constants.APIS.TELEPHONY.ENDTHIRDPARTY,
      {
        ConferenceSid: conferenceSid,
        CustomerCallSid: callSid,
        AccountSid: accountSid,
        IsAgentOnConsult: isAgentOnConsult,
      },
      { context: getContext('telephony') }
    );
  }
  hold(conferenceSid: string, callSid: string, agentCallSid: string, accountSid: string, onHold: boolean) {
    return this.http.post(
      Constants.APIS.TELEPHONY.HOLD,
      {
        ConferenceSid: conferenceSid,
        CustomerCallSid: callSid,
        AgentCallSid: agentCallSid,
        Hold: onHold,
        AccountSid: accountSid,
      },
      { context: getContext('telephony') }
    );
  }
  endCall(callSid: string, userId: string, date: string, leadId: string, data: any) {
    return this.http.disableLoader().post(Constants.APIS.TELEPHONY.ENDCALL, {
      UserId: userId,
      CallSid: callSid,
      Date: date,
      LeadId: leadId,
      Integration: data,
    });
  }
  warnTransferCall(conferenceSid: string, customerCallSid: string, accountSid: string, agentCallSid: string) {
    return this.http.post(
      Constants.APIS.TELEPHONY.WARM_TRANSFER,
      {
        ConferenceSid: conferenceSid,
        CustomerCallSid: customerCallSid,
        AgentCallSid: agentCallSid,
        AccountSid: accountSid,
      },
      { context: getContext('telephony') }
    );
  }
  coldTransferCall(
    conferenceSid: string,
    customerCallSid: string,
    accountSid: string,
    agentCallSid: string,
    number: string
  ) {
    return this.http.post(
      Constants.APIS.TELEPHONY.COLD_TRANSFER,
      {
        ConferenceSid: conferenceSid,
        CustomerCallSid: customerCallSid,
        AgentCallSid: agentCallSid,
        AccountSid: accountSid,
        TransferNumber: number,
      },
      { context: getContext('telephony') }
    );
  }
  assignCall(callSid: string, userId: string) {
    return this.http.disableLoader().post(Constants.APIS.CALL.ASSIGN, {
      CallSid: callSid,
      UserId: userId,
    });
  }
  getQueuedCalls(accountSid: string, campaigns: string, direction: number, selectedDialingCountry: string | null) {
    const url = `${Constants.APIS.TELEPHONY.QUEUED_CALLS}?AccountSid=${accountSid}&Campaigns=${campaigns}&Direction=${direction}&CountryCode=${selectedDialingCountry}`;
    return this.http
      .disableLoader()
      .get(url, { context: getContext('telephony') })
      .pipe(
        map((data: any) => {
          return {
            count: data.Count,
            leads: data.Leads.map((d: any) => {
              return {
                leadId: d.LeadId,
                email: d.Email,
                campaign: d.Campaign,
                callSid: d.CallSid,
                duration: d.Duration,
                to: d.To,
                from: d.From,
                callDirection: d.CallDirection,
                toCountry: d.ToCountry,
                fromCountry: d.FromCountry,
                taskSid: d.TaskSid,
                dialedCount: d.DialedCount,
              };
            }),
          };
          // return data.map((d: any) => {
          //   return {
          //     campaign: d.Campaign,
          //     callSid: d.CallSid,
          //     duration: d.Duration,
          //     to: d.To,
          //     from: d.From,
          //     callDirection: d.CallDirection,
          //     toCountry: d.ToCountry,
          //     fromCountry: d.FromCountry,
          //     taskSid: d.TaskSid,
          //   };
          // });
        })
      );
  }
  monitorCall(customerCallSid: string, accountSid: string, isWhisper: boolean, isBarge: boolean) {
    return this.http.post(
      Constants.APIS.TELEPHONY.MONITOR_CALL,
      {
        CustomerCallSid: customerCallSid,
        AccountSid: accountSid,
        IsWhisper: isWhisper,
        IsBarge: isBarge,
      },
      { context: getContext('telephony') }
    );
  }
  fetchCall(customerCallSid: string, accountSid: string) {
    return this.http.disableErrorHandler().post(
      Constants.APIS.TELEPHONY.FETCH_CALL,
      {
        CallSid: customerCallSid,
        AccountSid: accountSid,
      },
      { context: getContext('telephony') }
    );
  }
  updateWorkerActivity(accountSid: string, workerSid: string, activitySid: string) {
    return this.http.disableLoader().post(
      Constants.APIS.TELEPHONY.UPDATE_ACTIVITY,
      {
        AccountSid: accountSid,
        WorkerSid: workerSid,
        ActivitySid: activitySid,
      },
      { context: getContext('telephony') }
    );
  }
  addUserStatusDB(data: any) {
    return this.http.disableLoader().post<any>(Constants.APIS.USER.STATUS_LOG, data);
  }
  forceDisposedCall(callSid: string, userId: string) {
    return this.http
      .disableLoader()
      .post<any>(Constants.APIS.TELEPHONY.FORCE_DISPOSED, { CallSid: callSid, UserId: userId });
  }
  updateLeadStatus(leadId: string) {
    return this.http.disableLoader().post<any>(Constants.APIS.TELEPHONY.UPDATE_LEAD_STATUS, { LeadId: leadId });
  }
}
